import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { UnitNodePermissions } from "../unit/unitNodePermissions";
import { getApiCall, postApiCall } from "./tokenService";
import { CalendarTaskPage } from "../calendarTask/calendarTaskPage";
import { CalendarTaskItems } from "ui/Scripts/source/models/calendarTask/calendarTaskItems";
import { CalendarTaskCategoryListItems } from "ui/Scripts/source/models/calendarTaskCategory/calendarTaskCategoryListItems";
import { CalendarTaskCategoryEdit } from "ui/Scripts/source/models/calendarTaskCategory/calendarTaskCategoryEdit";
import { SaveSuccess } from "ui/Scripts/source/models/common/saveSuccess";
import { CalendarTaskEdit } from "ui/Scripts/source/models/calendarTask/calendarTaskEdit";
import { CalendarTaskPrivacyFilter, DatabaseRecordType } from "ui/Scripts/source/models/enums";
import { ICalendarTaskHandlerSave } from "ui/Scripts/source/models/calendarTaskHandler/calendarTaskHandlerSave";
import { SaveSuccessItems } from "ui/Scripts/source/models/common/saveSuccessItems";
import { UserGroupListItems } from "ui/Scripts/source/models/userGroup/userGroupListItems";
import { CalendarTaskPageData } from "../calendarTask/calendarTaskPageData";
import { CalendarTaskListItems } from "ui/Scripts/source/models/calendarTask/calendarTaskListItems";
import { CalendarTaskAmount } from "ui/Scripts/source/models/calendarTask/calendarTaskAmount";
import { CalendarTaskPermissions } from "ui/Scripts/source/models/calendarTask/calendarTaskPermissions";

// #region CalendarTask
export const getCalendarTaskPage = (): Promise<CalendarTaskPage> => {
    return getApiCall("api/calendartask", CalendarTaskPage);
};

export const getCalendarTaskPageData = (companyId: string): Promise<CalendarTaskPageData> => {
    return getApiCall("api/calendartask/pagedata?id=" + companyId, CalendarTaskPageData);
};

export const getCalendarTaskItems = (companyId: string, startDate: number, endDate: number,
    filter: string, states: number[], categoryIds: string[], handlerUserGroupCodes: string[], privacy: number, createUserCodes: string[]): Promise<CalendarTaskItems> => {
    return postApiCall("api/calendartask/items", CalendarTaskItems, JSON.stringify({
        companyId,
        startDate,
        endDate,
        filter: filter || "",
        states,
        categoryIds,
        handlerUserGroupCodes,
        isPrivate: privacy < CalendarTaskPrivacyFilter.Private ? null : privacy === CalendarTaskPrivacyFilter.Private,
        createUserCodes
    }));
};

export const getCalendarTaskListItems = (companyId: string, databaseRecordType: number, recordId: string, pageSize: number, page: number): Promise<CalendarTaskListItems> => {
    return postApiCall("api/calendartask/list", CalendarTaskListItems, JSON.stringify({
        companyId,
        databaseRecordType,
        recordId,
        pageSize,
        page,
    }));
};

export const getCalendarTaskAmount = (companyId: string, databaseRecordType: number, recordId: string, states: number[]): Promise<CalendarTaskAmount> => {
    return postApiCall("api/calendartask/amount", CalendarTaskAmount, JSON.stringify({
        companyId,
        databaseRecordType,
        recordId,
        states
    }));
};

export const getCalendarTaskPermissions = (companyId: string, calendarTaskId: string, recordId: string, databaseRecordType: DatabaseRecordType): Promise<CalendarTaskPermissions> => {
    return getApiCall("api/calendartask/permissions?id=" + calendarTaskId + "&companyId=" + companyId + "&recordId=" + (recordId ?? null) + "&databaseRecordType=" + databaseRecordType, CalendarTaskPermissions);
};
// #endregion CalendarTask

// #region Edit
export const getCalendarTaskEdit = (companyId: string, calendarTaskId: string, recordId: string, databaseRecordType: DatabaseRecordType): Promise<CalendarTaskEdit> => {
    return getApiCall("api/calendartask/edit?id=" + calendarTaskId + "&companyId=" + companyId + "&recordId=" + (recordId ?? null) + "&databaseRecordType=" + databaseRecordType, CalendarTaskEdit);
};

export const saveCalendarTaskEdit = (obj: any): Promise<SaveSuccess> => {
    return postApiCall("api/calendartask/editsave", SaveSuccess, JSON.stringify(obj));
};

export const removeCalendarTask = (companyId: string, calendarTaskId: string): Promise<ApiSuccess> => {
    return postApiCall("api/calendartask/remove?id=" + calendarTaskId + "&companyId=" + companyId, ApiSuccess);
};

export const setCalendarTaskStartDate = (companyId: string, calendarTaskId: string, startDate: number): Promise<ApiSuccess> => {
    return postApiCall("api/calendartask/setstartdate?id=" + calendarTaskId + "&companyId=" + companyId + "&startDate=" + startDate, ApiSuccess);
};
// #endregion Edit

// #region Edit Handlers
export const calendarTaskEditAddHandlers = (companyId: string, calendarTaskId: string, handlers: ICalendarTaskHandlerSave[]): Promise<ApiSuccess> => {
    return postApiCall("api/calendartask/editaddhandlers", ApiSuccess, JSON.stringify({
        companyId,
        id: calendarTaskId,
        handlers
    }));
};

export const calendarTaskEditRemoveHandlers = (companyId: string, calendarTaskId: string, handlers: ICalendarTaskHandlerSave[]): Promise<ApiSuccess> => {
    return postApiCall("api/calendartask/editremovehandlers", ApiSuccess, JSON.stringify({
        companyId,
        id: calendarTaskId,
        handlers
    }));
};
// #endregion Edit Handlers

// #region Edit Attachment
export const calendarTaskEditRemoveAttachments = (data: any): Promise<ApiSuccess> => {
    return postApiCall("api/calendartask/editremoveattachments", ApiSuccess, JSON.stringify(data));
};

export const calendarTaskEditAddAttachments = (data: any): Promise<SaveSuccessItems> => {
    return postApiCall("api/calendartask/editaddattachments", SaveSuccessItems, JSON.stringify(data));
};
// #endregion Edit Attachment

// #region Module properties
export const getUnitNodePermissions = (companyId: string): Promise<UnitNodePermissions> => {
    return getApiCall("api/calendartask/module/permissions?id=" + companyId, UnitNodePermissions);
};

export const saveUnitNodePermissions = (obj: any): Promise<ApiSuccess> => {
    return postApiCall("api/calendartask/module/savepermissions", ApiSuccess, JSON.stringify(obj));
};
// #endregion Module properties

// #region Module calendar task categories
export const getCalendarTaskCategoryListItems = (companyId: string, pageSize: number, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean): Promise<CalendarTaskCategoryListItems> => {
    return postApiCall("api/calendartask/module/category/list", CalendarTaskCategoryListItems, JSON.stringify({
        companyId,
        pageSize,
        page,
        sortColumn,
        sortOrder: sortOrderIsAsc ? "asc" : "desc",
        filter: filter || ""
    }));
};

export const getCalendarTaskCategoryEdit = (companyId: string, id: string): Promise<CalendarTaskCategoryEdit> => {
    return getApiCall("api/calendartask/module/category/edit?id=" + id + "&companyId=" + companyId, CalendarTaskCategoryEdit);
};

export const saveCalendarTaskCategoryEdit = (obj: any): Promise<SaveSuccess> => {
    return postApiCall("api/calendartask/module/category/save", SaveSuccess, JSON.stringify(obj));
};

export const removeCalendarTaskCategories = (obj: any): Promise<ApiSuccess> => {
    return postApiCall("api/calendartask/module/category/remove", ApiSuccess, JSON.stringify(obj));
};
// #endregion Module calendar task categories

// #region Select user groups
export const getUserGroupListItems = (companyId: string, pageSize: number, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean, groupType: number,
    ignoreUserGroupCodes: string[], requireUserGroupCodes: string[]): Promise<UserGroupListItems> => {
    return postApiCall("api/calendartask/getusergrouplistitems", UserGroupListItems, JSON.stringify({
        companyId,
        pageSize,
        page,
        sortColumn,
        sortOrder: sortOrderIsAsc ? "asc" : "desc",
        filter: filter || "",
        groupType: groupType || 0,
        ignoreUserGroupCodes: ignoreUserGroupCodes || [],
        requireUserGroupCodes: requireUserGroupCodes || []
    }));
};
// #endregion Select user groups
